import { useEffect, useRef, useState } from "react";
import Footer from "../../SharedModule/components/Footer";
import { Spinner } from "../../SharedModule/components/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import {
  CLIENTS_ALL_ROLES,
  CLIENTS_RELATIONSHIP_ADMIN_ACCESS,
  ROLE_CLIENT_ADMIN,
  ROLE_SUPER_ADMIN,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { ClientRelationshipReview } from "../types/clients-module-types";
import { useClientRelationshipReviewList } from "../hooks/useClientRelationshipReviewsList";
import {
  changeOrder,
  SelectOrderCriteria,
  setShowActives,
  setShowActivesInTab,
} from "../redux/reducers/clientRelationshipReviewsList.reducer";
import { TbEdit } from "react-icons/tb";
import { MdFileDownloadDone, MdDoNotDisturb } from "react-icons/md";
import { ModalAddEditRelationshipReview } from "../components/ModalAddEditRelationshipReview";
import { ModalPortal } from "../../SharedModule/components/ModalPortal";
import { useChangeStatusRelationship } from "../hooks/useChangeStatusRelationship";
import { useModal } from "../../SharedModule/hooks/useModal";
import eventHandler from "../../SharedModule/utils/eventHandler";
import { setNeedUpdateKeyContactList } from "../redux/reducers/clientKeyContacts.reducer";
import { useNavigate } from "react-router";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientsRelationshipReviewsListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sortBy = useSelector(SelectOrderCriteria);

  useEffect(() => {
    document.title = "The Hub - Client Relationship Reviews";

    dispatch(
      changeOrder({
        orderBy: "name",
        dataType: "string",
        criteria: "asc",
      })
    );

    dispatch(setActiveTab(ROUTE_CLIENTS_RELATIONSHIP_REVIEW));

    let posY = localStorage.getItem("relationshipScrollY");
    if (posY)
      setTimeout(() => {
        window.scrollTo({
          top: Number(posY),
          behavior: "smooth",
        });
      }, 300);

    eventHandler.on("toast action", (data) => {
      if (data.isClosed) {
        setIsToastClosed(true);
      } else {
        setIsToastClosed(false);
      }
    });

    return () => {
      resetScrollY(globalThis.location.pathname);
      eventHandler.remove("toast action", null);
    };
  }, []);

  const {
    clientRelationshipReview,
    isLoadingReviews,
    isShowingActives,
    isApplySupervisorCheck,
    costCenterOwnedIds,
    rolesLoaded,
    userRolesList,
    loadReviews,
    setIsLoadingReviews,
    isClientsLoading,
    clients,
    isLoadingAllCostCenters,
    allCostCenters,
  } = useClientRelationshipReviewList();

  useEffect(() => {
    if (!isLoadingReviews) {
      let posY = localStorage.getItem("relationshipScrollY");
      if (posY)
        setTimeout(() => {
          window.scrollTo({
            top: Number(posY),
            behavior: "smooth",
          });
        }, 300);
    }
  }, [isLoadingReviews]);

  const [seeActiveReviews, setSeeActiveReviews] = useState(isShowingActives);
  const [editMode, setEditMode] = useState(true);
  const [reviewToEdit, setReviewToEdit] = useState<any>(null);

  const [isToastClosed, setIsToastClosed] = useState(true);

  let clientRelationshipReviewToShow: Array<ClientRelationshipReview> =
    clientRelationshipReview.filter((review) => {
      return seeActiveReviews ? review.isActive : !review.isActive;
    });

  const onToggleButton = (state: boolean) => {
    setSeeActiveReviews(state);
    dispatch(setShowActives(!seeActiveReviews));
  };

  // Modal config
  const modalRef = useRef<undefined>(undefined);
  const showAddModal = () => {
    (modalRef.current as any).show(true);
  };
  const closeAddModal = (refresh: boolean, isCreate: boolean) => {
    (modalRef.current as any).show(false);
    if (isCreate) setSeeActiveReviews(true);
    if (refresh) {
      dispatch(setNeedUpdateKeyContactList(true));
      loadReviews(true);
    }
  };

  const addRelationshipReview = () => {
    setEditMode(false);
    setReviewToEdit(null);
    showAddModal();
  };

  const { showModal, closeModal, Modal, ...modalProps } = useModal();
  const changeStatusRelationship = useChangeStatusRelationship(
    showModal,
    closeModal,
    seeActiveReviews,
    setSeeActiveReviews,
    setIsLoadingReviews
  );

  const handleReview = async (event: any, review: ClientRelationshipReview) => {
    event.stopPropagation();
    // Return a boolean value if it's in inactive view, we should set ViewInactives to false because we reload products list
    await changeStatusRelationship({ review: review });
    dispatch(setNeedUpdateKeyContactList(true));
    loadReviews(true);
  };

  const handleEdit = (event: any, review: ClientRelationshipReview) => {
    event.stopPropagation();
    setEditMode(true);
    setReviewToEdit(review);
    showAddModal();
  };

  const isAbleToAddRelationship = () => {
    return (
      verifyModuleAccess(
        true,
        [ROLE_SUPER_ADMIN, ROLE_CLIENT_ADMIN],
        userRolesList
      ) ||
      (isApplySupervisorCheck &&
        allCostCenters.some((cc) => costCenterOwnedIds.includes(cc.value)))
    );
  };

  const goToRelationshipTab = (review: ClientRelationshipReview) => {
    localStorage.setItem("relationshipScrollY", window.scrollY.toString());
    dispatch(setShowActivesInTab(review.isActive));
    navigate(`/clients/${review.clientId}/relationshipReviews`, {
      state: { from: ROUTE_CLIENTS_RELATIONSHIP_REVIEW },
    });
  };

  return (
    <>
      {(!rolesLoaded || isLoadingReviews || !isToastClosed) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {rolesLoaded &&
        !isLoadingReviews &&
        isToastClosed &&
        !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {rolesLoaded &&
        !isLoadingReviews &&
        isToastClosed &&
        verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <>
            <Modal closeModal={closeModal} {...modalProps} />
            <ModalPortal ref={modalRef}>
              <ModalAddEditRelationshipReview
                btnCancel={closeAddModal}
                actionPlaceholder="notCloseOnClickOutside"
                clients={clients}
                isClientsLoading={isClientsLoading}
                costCenters={allCostCenters}
                isLoadingAllCostCenters={isLoadingAllCostCenters}
                editMode={editMode}
                isClientTabPage={false}
                reviewToEdit={reviewToEdit}
                clientId={null}
                costCenterOwnedIds={
                  isApplySupervisorCheck ? costCenterOwnedIds : null
                }
              />
            </ModalPortal>
            <div className="content mt-4">
              <div className="content-header" style={{ marginTop: "0" }}>
                <h2>
                  Relationship Reviews
                  <span
                    className="helper-title ms-3"
                    style={{ fontSize: "14px" }}
                  >
                    ({`${clientRelationshipReview.length} reviews`})
                  </span>
                </h2>

                <div className="d-flex mb-3 justify-content-between">
                  <fieldset
                    className="flex-column"
                    style={{ marginTop: "3px" }}
                  >
                    <div className="btn-group w-100">
                      <button
                        type="button"
                        onClick={() => onToggleButton(true)}
                        className={`btn btn-group-custom ${
                          !seeActiveReviews ? "btn-light" : "btn-active"
                        }`}
                        disabled={seeActiveReviews || isLoadingReviews}
                      >
                        Active
                      </button>
                      <button
                        type="button"
                        onClick={() => onToggleButton(false)}
                        className={`btn btn-group-custom ${
                          !seeActiveReviews ? "btn-inactive" : "btn-light"
                        }`}
                        disabled={!seeActiveReviews || isLoadingReviews}
                      >
                        Inactive
                      </button>
                    </div>
                  </fieldset>
                  {isAbleToAddRelationship() && (
                    <div
                      className="flex-column ms-5"
                      style={{ marginTop: "auto" }}
                    >
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => addRelationshipReview()}
                        disabled={!isToastClosed}
                      >
                        Add Relationship Review
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {clientRelationshipReviewToShow.length === 0 && (
                <div className="py-5">
                  <NoContent
                    title="No Results"
                    text="Edit your filters to find the relationship reviews you are looking for."
                    image={image}
                    style={{ margin: "0 auto" }}
                  />
                </div>
              )}

              {clientRelationshipReviewToShow.length > 0 && (
                <table
                  className="table grey-table-header-background mt-4 mb-7"
                  style={{ width: "100%" }}
                >
                  <thead className="align-middle sticky sticky-header">
                    <tr>
                      <th
                        className={`border-dark border-top fw-600 ps-5 column-35 pointer ${
                          sortBy.orderBy === "name"
                            ? "sorted-" + sortBy.criteria
                            : ""
                        }`}
                        key="name"
                        id="name"
                        onClick={() =>
                          dispatch(
                            changeOrder({
                              orderBy: "name",
                              dataType: "string",
                            })
                          )
                        }
                      >
                        Name
                      </th>
                      <th
                        className={`border-dark border-top fw-600 ps-5 column-35 pointer ${
                          sortBy.orderBy === "clientName"
                            ? "sorted-" + sortBy.criteria
                            : ""
                        }`}
                        key="clientName"
                        id="clientName"
                        onClick={() =>
                          dispatch(
                            changeOrder({
                              orderBy: "clientName",
                              dataType: "string",
                            })
                          )
                        }
                      >
                        Client
                      </th>
                      <th
                        className={`border-dark border-top fw-600 ps-5 column-20 pointer ${
                          sortBy.orderBy === "costCenterId"
                            ? "sorted-" + sortBy.criteria
                            : ""
                        }`}
                        key="costCenterId"
                        id="costCenterId"
                        onClick={() =>
                          dispatch(
                            changeOrder({
                              orderBy: "costCenterId",
                              dataType: "number",
                            })
                          )
                        }
                      >
                        Cost Center
                      </th>
                      <th className="border-dark border-top column-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientRelationshipReviewToShow.map(
                      (review: ClientRelationshipReview) => {
                        return (
                          <tr
                            key={review.id}
                            className="product-link"
                            onClick={() => goToRelationshipTab(review)}
                          >
                            <td className="ps-5 fw-500 td-shadow-effect">
                              {review.name}
                            </td>
                            <td className="ps-5 fw-500 td-shadow-effect">
                              {review.clientName}{" "}
                              {review.clientActive ? "" : "(X)"}
                            </td>
                            <td className="ps-5 fw-500 td-shadow-effect">
                              {review.costCenterId} - {review.costCenterName}{" "}
                              {allCostCenters.some(
                                (cc) => cc.value === review.costCenterId
                              )
                                ? ""
                                : "(x)"}
                            </td>
                            <td className="td-shadow-effect">
                              {verifyModuleAccess(
                                true,
                                CLIENTS_RELATIONSHIP_ADMIN_ACCESS,
                                userRolesList
                              ) &&
                                review.clientActive &&
                                ((isApplySupervisorCheck &&
                                  costCenterOwnedIds.includes(
                                    review.costCenterId
                                  )) ||
                                  !isApplySupervisorCheck) && (
                                  <>
                                    {review.isActive && (
                                      <TbEdit
                                        size={"20px"}
                                        style={{ margin: "0 15px 5px 0" }}
                                        className="purpleHover iconsRow"
                                        onClick={(e) => handleEdit(e, review)}
                                      />
                                    )}
                                    {seeActiveReviews && review.isActive && (
                                      <MdDoNotDisturb
                                        size={"20px"}
                                        style={{ margin: "0 15px 5px 0" }}
                                        className="redHover iconsRow"
                                        onClick={(e) => handleReview(e, review)}
                                      />
                                    )}
                                    {!seeActiveReviews && !review.isActive && (
                                      <MdFileDownloadDone
                                        size={"20px"}
                                        style={{ margin: "0 15px 5px 0" }}
                                        className="purpleHover iconsRow"
                                        onClick={(e) => handleReview(e, review)}
                                      />
                                    )}
                                  </>
                                )}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              )}
              <Footer />
            </div>
          </>
        )}
    </>
  );
};
